import { useMemo, useState } from 'react';
import { GuestRequests } from '../components/GuestRequests.js';
import { OtherVenueNotice } from '../components/OtherVenueNotice.js';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type ReservationRow } from '../lib/api.js';
import { useSession, useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { currentServiceDate, formatTime } from '../lib/time.js';
import { ServiceDatePicker } from '../components/ServiceDatePicker.js';
import { StatusPill } from '../components/StatusPill.js';
import { BookingDrawer } from '../components/BookingDrawer.js';

/**
 * The reservation list.
 *
 * Built for a rush: status changes are inline, one tap, with no confirmation
 * dialog in the way. A host moving twelve parties through "arrived" during a
 * seating peak cannot afford a modal each time.
 *
 * The safety net is optimistic concurrency rather than a prompt — if another
 * host got there first the API returns 409 and this reloads and says so, which
 * is both faster and safer than asking "are you sure?" twelve times.
 */

const STATUS_FILTERS = [
  'pending',
  'confirmed',
  'reconfirmed',
  'arrived',
  'seated',
  'finished',
  'no_show',
  'cancelled_by_guest',
] as const;

const ACTIONS: Array<{ path: string; labelKey: string; permission: string; from: string[] }> = [
  { path: 'arrive', labelKey: 'actions.arrive', permission: 'reservation:seat', from: ['pending', 'confirmed', 'reconfirmed'] },
  { path: 'seat', labelKey: 'actions.seat', permission: 'reservation:seat', from: ['confirmed', 'reconfirmed', 'arrived'] },
  { path: 'finish', labelKey: 'actions.finish', permission: 'reservation:seat', from: ['seated', 'dessert', 'check_dropped', 'arrived'] },
  { path: 'no-show', labelKey: 'actions.noShow', permission: 'reservation:no_show', from: ['pending', 'confirmed', 'reconfirmed'] },
  { path: 'cancel', labelKey: 'actions.cancel', permission: 'reservation:cancel', from: ['pending', 'confirmed', 'reconfirmed', 'arrived'] },
];

export function ReservationsScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const { can } = useSession();
  const queryClient = useQueryClient();

  const [serviceDate, setServiceDate] = useState(() =>
    venue ? currentServiceDate(venue) : '',
  );
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const date = serviceDate || (venue ? currentServiceDate(venue) : '');

  const list = useQuery({
    queryKey: ['reservations', venueId, date, statusFilter],
    enabled: Boolean(venueId && date),
    queryFn: () =>
      api.get<{ reservations: ReservationRow[] }>('/api/v1/reservations', {
        venue_id: venueId,
        date,
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
    refetchInterval: 30_000,
  });

  const act = useMutation({
    mutationFn: (input: { id: string; path: string; version: number }) =>
      api.post(`/api/v1/reservations/${input.id}/${input.path}`, { version: input.version }),
    onSuccess: () => {
      setNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
    onError: (err: unknown) => {
      // A stale version is not a failure the host caused. Say what happened and
      // reload, rather than showing a generic error.
      setNotice(
        err instanceof ApiError && err.isVersionConflict
          ? t('errors.versionConflict')
          : err instanceof ApiError
            ? err.message
            : t('errors.generic'),
      );
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });

  const rows = useMemo(() => list.data?.reservations ?? [], [list.data]);

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  return (
    <div className="flex flex-col gap-4">
      {/* Renders nothing unless this venue is empty for the date and another
          one is not. See the component for why it exists. */}
      <OtherVenueNotice date={date} />

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{t('reservations.title')}</h1>
          <ServiceDatePicker value={date} onChange={setServiceDate} venue={venue} />
        </div>

        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="status-filter">
            {t('reservations.filterStatus')}
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
          >
            <option value="">{t('reservations.all')}</option>
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>
                {t(`status.${status}`)}
              </option>
            ))}
          </select>

          {can('reservation:create') ? (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="rounded-md bg-brand-teal px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-teal-dark"
            >
              {t('reservations.newBooking')}
            </button>
          ) : null}
        </div>
      </header>

      {notice ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
          {notice}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th scope="col" className="px-3 py-2 text-start">{t('reservations.time')}</th>
              <th scope="col" className="px-3 py-2 text-start">{t('reservations.guest')}</th>
              <th scope="col" className="px-3 py-2 text-start">{t('reservations.size')}</th>
              <th scope="col" className="px-3 py-2 text-start">{t('reservations.ref')}</th>
              <th scope="col" className="px-3 py-2 text-start">{t('reservations.status')}</th>
              <th scope="col" className="px-3 py-2 text-start">{t('reservations.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((booking) => (
              <tr key={booking.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-2 font-mono" data-numeric>
                  {formatTime(booking.starts_at, venue, i18n.language)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {[booking.guest?.first_name, booking.guest?.last_name]
                        .filter(Boolean)
                        .join(' ') ||
                        booking.guest?.name_ar ||
                        t('common.guest')}
                    </span>
                    {/* Allergies ride with the name everywhere they appear. A
                        host should never have to open a row to find one. */}
                    {booking.guest?.allergies ? (
                      <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700">
                        {booking.guest.allergies}
                      </span>
                    ) : null}
                    {booking.occasion ? (
                      <span className="rounded bg-brand-bronze-light px-1.5 py-0.5 text-xs text-brand-bronze-dark">
                        {booking.occasion}
                      </span>
                    ) : null}
                  </div>
                  {/* Children, seating, a requested table, a company. Renders
                      nothing when the guest asked for nothing. */}
                  <GuestRequests booking={booking} />
                </td>
                <td className="px-3 py-2 font-mono" data-numeric>
                  {booking.party_size}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">
                  {booking.booking_ref}
                </td>
                <td className="px-3 py-2">
                  <StatusPill status={booking.status} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {ACTIONS.filter(
                      (action) => action.from.includes(booking.status) && can(action.permission),
                    ).map((action) => (
                      <button
                        key={action.path}
                        type="button"
                        disabled={act.isPending}
                        onClick={() =>
                          act.mutate({
                            id: booking.id,
                            path: action.path,
                            version: booking.version,
                          })
                        }
                        className="rounded border border-slate-200 px-2 py-0.5 text-xs hover:bg-brand-teal-light disabled:opacity-50"
                      >
                        {t(action.labelKey)}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {rows.length === 0 && !list.isLoading ? (
          <p className="p-6 text-center text-sm text-slate-500">{t('reservations.none')}</p>
        ) : null}
      </div>

      {drawerOpen ? (
        <BookingDrawer
          serviceDate={date}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['reservations'] });
            void queryClient.invalidateQueries({ queryKey: ['floor'] });
          }}
        />
      ) : null}
    </div>
  );
}
